import * as React from "react";
import classNames from "classnames";
import ReactSelectClass from "react-select";
import Select from "react-select";
import AsyncSelect from "react-select/lib/Async";
import { IBaseFormInputProps } from "../";
import { FontAwesomeIcons, FontAwesomeIcon } from "../../../../constants/fontAwesomeIcons";
import { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { PersonnelOption } from "src/dtos/Tmd.dtos";

import "./SelectFormInput.scss";

export interface ISelectFormInputProps {
    loading: boolean;
    multi: boolean;
    options?: any[];
    loadOptions?: (inputValue: string) => Promise<any[]>;
    clearable: boolean;
    backspaceRemoves: boolean;
    matchPos?: string;
    placeholder?: string;
    customArrowRender?: string;
    maxOptionValueDisplay?: number;
    feedbackIcon?: IconDefinition;
    hideFeedback?: boolean;
    modified?: boolean;
    defaultOptions?: any[] | boolean;
}

interface ISelectFormInputState {
    selectedOption: 
        | null 
        | { label: string; value: any } // single select
        | { label: string; value: any }[]; // multi-select
}

class SelectFormInput extends React.Component<
    ISelectFormInputProps & IBaseFormInputProps,
    ISelectFormInputState
> {
    private asyncOptionsCache: Record<string, any[]> = {};

    constructor(props) {
        super(props);

        this.state = {
            selectedOption: null,
        };

        this.onMultiSelectChange = this.onMultiSelectChange.bind(this);
        this.onSelectChange = this.onSelectChange.bind(this);
        this.loadOptions = this.loadOptions.bind(this);
    }

    // Cached async loader
    async loadOptions(inputValue: string) {
        const { loadOptions } = this.props;
        if (!loadOptions) return [];

        // Return cached result if exists
        if (this.asyncOptionsCache[inputValue]) {
            return this.asyncOptionsCache[inputValue];
        }

        const options = await loadOptions(inputValue);

        // Cache for future
        this.asyncOptionsCache[inputValue] = options;

        return options;
    }

    componentDidMount() {
        this.initializeSelectedOption();
    }

    componentDidUpdate(prevProps: ISelectFormInputProps & IBaseFormInputProps) {
        if (prevProps.value !== this.props.value) {
            this.initializeSelectedOption();
        }
    }

    private initializeSelectedOption() {
        const { value, loadOptions, multi } = this.props;

        if (loadOptions && value) {
            if (multi && Array.isArray(value) && value.length > 0) {
                // multi-select with label/value pairs
                if (value.every(v => typeof v === 'object' && 'label' in v && 'value' in v)) {
                    this.setState({ selectedOption: value });
                    return;
                }
            }
            else if (!multi && typeof value === 'object' && 'label' in value && 'value' in value) {
                // single-select with label/value pair
                this.setState({ selectedOption: value });
                return;
            }
        }
        else if (value && multi && !loadOptions) {
            if (value.every(v => typeof v === 'object' && 'label' in v && 'value' in v)) {
                this.setState({ selectedOption: value });
                return;
            }
        }

        // Otherwise, fallback to async resolution
        if (loadOptions) {
            this.resolveSelectedOption();
        }
    }
    
    private async resolveSelectedOption() {
        const { value, loadOptions, multi } = this.props;

        if (!value || !loadOptions) {
            this.setState({ selectedOption: null });
            return;
        }

        // If already have label/value, skip
        if (Array.isArray(value) && value.every(v => v.label && v.value)) {
            this.setState({ selectedOption: value });
            return;
        }

        const values = multi ? (Array.isArray(value) ? value : [value]) : [value];
        let foundOptions: any[] = [];

        // Try cache first
        for (const key in this.asyncOptionsCache) {
            const cached = this.asyncOptionsCache[key]?.filter(opt => values.includes(opt.value)) ?? [];
            foundOptions.push(...cached);
        }

        // Find missing values
        const missingValues = values.filter(v => !foundOptions.some(o => o.value === v));
        if (missingValues.length > 0) {
            const options = await loadOptions("");
            const fetched = options.filter(opt => missingValues.includes(opt.value));
            foundOptions.push(...fetched);
        }

        const uniqueOptions = foundOptions.filter(
            (opt, idx, arr) => arr.findIndex(o => o.value === opt.value) === idx
        );

        const formattedOptions = uniqueOptions.map(opt => ({
            label: opt.label ?? String(opt.value),
            value: opt.value,
        }));

        this.setState({
            selectedOption: multi
                ? formattedOptions
                : formattedOptions[0] || null,
        });
    }



    render() {
        const {
            invalid,
            className,
            name,
            onBlur,
            multi,
            onFocus,
            value,
            disabled,
            loading,
            options,
            clearable,
            backspaceRemoves,
            matchPos,
            placeholder,
            feedbackIcon,
            hideFeedback,
            hasSoftError,
            hasHardError,
        } = this.props;

        const { selectedOption } = this.state;

        const derivedClassName = className ? className : undefined;

        const hardFeedback =
            hasHardError && !disabled ? (
                <span className={classNames("form-control-feedback block pr-2")}>
                    <FontAwesomeIcon
                        icon={
                            feedbackIcon
                                ? feedbackIcon
                                : FontAwesomeIcons.Solid.EXCLAMATION_CIRCLE
                        }
                        fixedWidth
                    />
                </span>
            ) : null;

        const feedback =
            invalid && !disabled && !hideFeedback && !hasHardError ? (
                <span className={classNames("form-control-feedback pr-2")}>
                    <FontAwesomeIcon
                        icon={
                            feedbackIcon
                                ? feedbackIcon
                                : FontAwesomeIcons.Solid.EXCLAMATION_CIRCLE
                        }
                        fixedWidth
                    />
                </span>
            ) : null;

        const softFeedback =
            hasSoftError && !disabled && !invalid ? (
                <span
                    className={classNames(
                        "form-control-feedback warning pr-12"
                    )}
                >
                    <FontAwesomeIcon
                        icon={
                            feedbackIcon
                                ? feedbackIcon
                                : FontAwesomeIcons.Solid.EXCLAMATION_TRIANGLE
                        }
                        fixedWidth
                    />
                </span>
            ) : null;

        const customStyles = {
            menuPortal: (base) => ({ ...base, zIndex: 9999 }),
            control: (baseStyles, state) => ({
                ...baseStyles,
                boxShadow: state.isFocused
                    ? invalid
                        ? "inset 0 1px 1px rgba(0, 0, 0, 0.075), 0 0 0 3px rgba(220, 53, 69, 0.25)"
                        : "inset 0 1px 1px rgba(0, 0, 0, 0.075), 0 0 0 3px rgba(86, 44, 133, 0.25)"
                    : null,
            }),
        };

        const isAsync = typeof this.props.loadOptions === "function";
        const SelectComponent = isAsync ? AsyncSelect : Select;

        const selectedValue = isAsync
            ? selectedOption
            : multi
                ? selectedOption
                : options
                    ?.flatMap(opt => (opt.options ? opt.options : [opt]))
                    ?.find(opt => opt.value === value);

        const selectInput = (
            <div className={classNames("form-select-group")}>
                <SelectComponent
                    className={classNames(
                        derivedClassName,
                        { "is-invalid": invalid && !disabled },
                        { "is-warning": hasSoftError && !disabled && !invalid },
                        { "is-modified": this.props.modified }
                    )}
                    menuPortalTarget={document.body}
                    styles={customStyles}
                    classNamePrefix="react-select"
                    name={name}
                    onBlur={onBlur}
                    onChange={multi ? this.onMultiSelectChange : this.onSelectChange}
                    onFocus={onFocus}
                    value={selectedValue ?? null}
                    isDisabled={disabled}
                    isLoading={loading}
                    isMulti={multi}
                    {...(isAsync
                        ? {
                                loadOptions: this.loadOptions,
                                cacheOptions: true,
                                defaultOptions: this.props.defaultOptions ?? true,
                        }
                        : {
                                options: options,
                        })}
                    isClearable={clearable}
                    backspaceRemoves={backspaceRemoves}
                    matchPos={matchPos}
                    placeholder={placeholder}
                    arrowRenderer={this.renderArrow()}
                />
            </div>
        );

        return (
            <div style={{ position: "relative" }}>
                {selectInput}
                {invalid && !disabled && (
                    <>
                        {hardFeedback}
                        {softFeedback}
                    </>
                )}
                {feedback}
            </div>
        );
    }

    onSelectChange(val: ReactSelectClass.Option) {
        if (!val) {
            this.props.onChange(null);
            this.setState({ selectedOption: null });
        } else {
            this.props.onChange(val.value);
            this.setState({ selectedOption: val });
        }
    }

    onMultiSelectChange(values: ReactSelectClass.Option[]) {
        const selectedValues = values?.map((option) => ({ label: option.label, value: option.value} as PersonnelOption)) ?? [];
        this.props.onChange(selectedValues);
    }

    renderArrow() {
        const { customArrowRender, invalid, hideFeedback } = this.props;

        if (invalid) {
            if (hideFeedback) {
                return undefined;
            }
            return null;
        }

        if (customArrowRender) {
            return customArrowRender;
        }

        return undefined;
    }
}

export default SelectFormInput;
